import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import '@fontsource-variable/instrument-sans';
import '@fontsource-variable/source-serif-4';
import '@fontsource-variable/jetbrains-mono';
import 'katex/dist/katex.min.css';
import '@cpu/design-system/theme.css';
import './styles.css';
import { router } from './app/router';
import { ThemeProvider } from './app/theme';
import { ComfortProvider, useComfort } from './app/comfort';
import { setSoundEnabled } from './app/sound';
import { startContentEnhancer } from './shared/contentEnhancer';

startContentEnhancer();

/**
 * Dev-server safety valve: service workers are registered per-ORIGIN, not
 * per-port, so a SW cached by any previous production preview on localhost
 * hijacks the Vite dev server and keeps serving stale assets (symptom:
 * "nothing changed no matter what gets fixed"). Purge everything in dev.
 */
if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  void navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) void registration.unregister();
  });
  void caches.keys().then((keys) => {
    for (const key of keys) void caches.delete(key);
  });
}

/** Bridges the comfort setting to the imperative sound engine. */
function SoundBridge() {
  const { comfort } = useComfort();
  useEffect(() => { setSoundEnabled(comfort.sound); }, [comfort.sound]);
  return null;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <ComfortProvider>
        <SoundBridge />
        <RouterProvider router={router} />
      </ComfortProvider>
    </ThemeProvider>
  </React.StrictMode>
);
